import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Button, Segmented, Tooltip } from 'antd';
import type { PageMode } from './MelodyAnalysis';
import { useTranslation } from 'react-i18next';

/** Zoom of the sheet relative to the music's original size (1); the buttons step through these levels. */
export const zoomLevels = [0.5, 0.67, 0.8, 1, 1.25, 1.5, 2, 2.5, 3];
export const minimumZoom = zoomLevels[0];
export const maximumZoom = zoomLevels[zoomLevels.length - 1];

export const clampZoom = (zoom: number) => Math.min(maximumZoom, Math.max(minimumZoom, zoom));

/** The next level up (1) or down (-1) from the zoom. */
export const nextZoom = (zoom: number, direction: 1 | -1) =>
  direction > 0
    ? (zoomLevels.find((level) => level > zoom + 0.01) ?? maximumZoom)
    : ([...zoomLevels].reverse().find((level) => level < zoom - 0.01) ?? minimumZoom);

/**
 * Zoom of the sheet (MelodyAnalysis lays the music out again at the new size): zooming in gives larger notes, fewer
 * bars per line and more pages; a page always fits the view, so the sheet never scrolls sideways.
 */
const SheetZoom = ({ onChange, zoom }: { onChange: (zoom: number) => void; zoom: number }) => {
  const { t: translate } = useTranslation();

  return (
    <div aria-label={translate('sheetZoom.group')} className="flex items-center" role="group">
      <Tooltip title={translate('sheetZoom.zoomOut')}>
        <Button
          aria-label={translate('sheetZoom.zoomOut')}
          disabled={zoom <= minimumZoom + 1e-3}
          icon={<ZoomOutOutlined />}
          onClick={() => onChange(nextZoom(zoom, -1))}
          size="small"
          type="text"
        />
      </Tooltip>
      <Tooltip title={translate('sheetZoom.original')}>
        <Button
          aria-label={translate('sheetZoom.original')}
          onClick={() => onChange(1)}
          size="small"
          style={{ fontVariantNumeric: 'tabular-nums', minWidth: 52 }}
          type="text"
        >
          {Math.round(zoom * 100)} %
        </Button>
      </Tooltip>
      <Tooltip title={translate('sheetZoom.zoomIn')}>
        <Button
          aria-label={translate('sheetZoom.zoomIn')}
          disabled={zoom >= maximumZoom - 1e-3}
          icon={<ZoomInOutlined />}
          onClick={() => onChange(nextZoom(zoom, 1))}
          size="small"
          type="text"
        />
      </Tooltip>
    </div>
  );
};

const pageModeKey = 'melodyvis.pageMode';

/** The page mode chosen last on this browser; A4 pages by default. */
export const storedPageMode = (): PageMode => {
  try {
    return window.localStorage.getItem(pageModeKey) === 'fit' ? 'fit' : 'a4';
  } catch {
    return 'a4';
  }
};

/** Switches the sheet between printed A4 pages and pages that fill the view (MelodyAnalysis). */
export const PageModeSwitch = ({
  onChange,
  pageMode
}: {
  onChange: (pageMode: PageMode) => void;
  pageMode: PageMode;
}) => {
  const { t: translate } = useTranslation();

  return (
    <Segmented<PageMode>
      aria-label={translate('pageMode.group')}
      onChange={(value) => {
        try {
          window.localStorage.setItem(pageModeKey, value);
        } catch {
          // Without storage the choice lasts until the page is reloaded.
        }
        onChange(value);
      }}
      options={[
        {
          label: <Tooltip title={translate('pageMode.a4Hint')}>{translate('pageMode.a4')}</Tooltip>,
          value: 'a4'
        },
        {
          label: (
            <Tooltip title={translate('pageMode.fitHint')}>{translate('pageMode.fit')}</Tooltip>
          ),
          value: 'fit'
        }
      ]}
      size="small"
      value={pageMode}
    />
  );
};

export default SheetZoom;
